import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Grid,
  makeStyles,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React from "react";
import { useApp } from "../context";
import { Category, Question } from "../parse";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  uploadButton: {
    marginBottom: theme.spacing(3),
  },
  batchControls: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  audioCard: {
    marginBottom: theme.spacing(2),
  },
  audioControls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  submitButton: {
    marginTop: theme.spacing(3),
  },
  formControl: {
    minWidth: 200,
    marginRight: theme.spacing(2),
  },
}));

interface AudioFile {
  file: File;
  id: string;
  name: string;
  synthetic: boolean | null;
}

export const AdminQuestionBatchUploadRoute = observer(
  function AdminQuestionBatchUploadRoute() {
    const app = useApp();
    const classes = useStyles();

    const [audioFiles, setAudioFiles] = React.useState<AudioFile[]>([]);
    const [selectedCategory, setSelectedCategory] = React.useState<string>("");
    const [isUploading, setIsUploading] = React.useState(false);

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files) return;

      const newAudioFiles: AudioFile[] = Array.from(files).map(
        (file, index) => ({
          file,
          id: `${Date.now()}-${index}`,
          name: file.name,
          synthetic: null,
        }),
      );

      setAudioFiles((prev) => [...prev, ...newAudioFiles]);
    };

    const updateAudioType = (id: string, synthetic: boolean) => {
      setAudioFiles((prev) =>
        prev.map((audio) =>
          audio.id === id ? { ...audio, synthetic } : audio,
        ),
      );
    };

    const batchUpdateAudioType = (synthetic: boolean) => {
      setAudioFiles((prev) => prev.map((audio) => ({ ...audio, synthetic })));
    };

    const removeAudioFile = (id: string) => {
      setAudioFiles((prev) => prev.filter((audio) => audio.id !== id));
    };

    const handleSubmit = async () => {
      setIsUploading(true);

      console.log("Submitting audio files:", audioFiles);

      try {
        for (let i = 0; i < audioFiles.length; i++) {
          const audio = audioFiles[i];
          if (audio.synthetic === null) continue;

          const QuestionE = Parse.Object.extend(Question.className);
          const newQuestion = new QuestionE();

          newQuestion.set(
            "audio",
            new Parse.File(audio.file.name, audio.file, audio.file.type),
          );
          newQuestion.set("name", audio.name.replace(/\.[^/.]+$/, "")); // Remove file extension
          newQuestion.set("category", {
            __type: "Pointer",
            className: Category.className,
            objectId: "xL8350AsXJ",
          });
          newQuestion.set("synthetic", audio.synthetic);

          console.log("new Question", newQuestion);

          newQuestion
            .save()
            .then(() => {
              console.log(`Question created for ${audio.name}`);
            })
            .catch((error: any) => {
              console.error(
                `Error creating question for ${audio.name}:`,
                error,
              );
            });
        }

        // Clear the form after successful upload
        setAudioFiles([]);
        setSelectedCategory("");

        // Refresh data by calling init
        await app.benchmarks.init({});
      } catch (error) {
        console.error("Error uploading questions:", error);
      } finally {
        setIsUploading(false);
      }
    };

    const canSubmit =
      audioFiles.length > 0 &&
      audioFiles.every((audio) => audio.synthetic !== null);

    return (
      <div className={classes.container}>
        <Typography variant="h4" gutterBottom>
          Batch Upload Questions
        </Typography>

        {/* Category Selection */}
        {/* <FormControl variant="outlined" className={classes.formControl}>
                    <InputLabel>Category</InputLabel>
                    <Select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value as string)}
                        label="Category"
                    >
                        {app.benchmarks.categories.map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                                {category.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl> */}

        {/* File Upload */}
        <Box className={classes.uploadButton}>
          <input
            accept="audio/*"
            style={{ display: "none" }}
            id="audio-upload"
            multiple
            type="file"
            onChange={handleFileUpload}
          />
          <label htmlFor="audio-upload">
            <Button variant="contained" color="primary" component="span">
              Upload Audio Files
            </Button>
          </label>
        </Box>

        {/* Batch Controls */}
        {audioFiles.length > 0 && (
          <Box className={classes.batchControls}>
            <Typography variant="h6" gutterBottom>
              Batch Actions
            </Typography>
            <Box display="flex" style={{ gap: "16px" }}>
              <Button
                variant="outlined"
                onClick={() => batchUpdateAudioType(false)}
              >
                Mark All as Real
              </Button>
              <Button
                variant="outlined"
                onClick={() => batchUpdateAudioType(true)}
              >
                Mark All as Synthetic
              </Button>
            </Box>
          </Box>
        )}

        {/* Audio Files List */}
        <Grid container spacing={2}>
          {audioFiles.map((audio) => (
            <Grid item xs={12} md={6} key={audio.id}>
              <Card className={classes.audioCard}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Typography variant="h6" component="div">
                      {audio.name}
                    </Typography>
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() => removeAudioFile(audio.id)}
                    >
                      Remove
                    </Button>
                  </Box>

                  <Box className={classes.audioControls}>
                    <audio controls src={URL.createObjectURL(audio.file)} />
                  </Box>

                  <Box mt={2}>
                    <Typography variant="body2" gutterBottom>
                      Audio Type:
                    </Typography>
                    <RadioGroup
                      row
                      value={
                        audio.synthetic === null
                          ? ""
                          : audio.synthetic.toString()
                      }
                      onChange={(e) =>
                        updateAudioType(audio.id, e.target.value === "true")
                      }
                    >
                      <FormControlLabel
                        value="false"
                        control={<Radio />}
                        label="Real"
                      />
                      <FormControlLabel
                        value="true"
                        control={<Radio />}
                        label="Synthetic"
                      />
                    </RadioGroup>
                  </Box>

                  {audio.synthetic !== null && (
                    <Box mt={1}>
                      <Chip
                        label={audio.synthetic ? "Synthetic" : "Real"}
                        color={audio.synthetic ? "secondary" : "primary"}
                        size="small"
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Submit Button */}
        {audioFiles.length > 0 && (
          <Button
            className={classes.submitButton}
            variant="contained"
            color="primary"
            size="large"
            disabled={!canSubmit || isUploading}
            onClick={handleSubmit}
          >
            {isUploading
              ? "Uploading..."
              : `Create ${audioFiles.length} Questions`}
          </Button>
        )}

        {/* Status */}
        {audioFiles.length > 0 && (
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              {audioFiles.filter((a) => a.synthetic !== null).length} of{" "}
              {audioFiles.length} files configured
            </Typography>
          </Box>
        )}
      </div>
    );
  },
);
